'use client'

import { Box, SxProps, Theme, useTheme } from '@mui/material'
import React, { ReactElement, ReactNode, useRef, useState } from 'react'
import { tss } from 'tss-react/mui'
import useIsomorphicLayoutEffect from '../useLayoutEffect'
import useWindowSize from '../useWindowSize'

const { useLayoutEffect } = useIsomorphicLayoutEffect

export type CardSliderProps = {
    sx?: SxProps<Theme>
    scrollableMarginRight?: number
    children: ReactNode
    parentHorizontalSpacing?: number
}

const MIN_CARD_WIDTH = 180
const SAFE_ZONE_WIDTH = 15 // a pixel amount to the left and right of each card that the cutoff should avoid
const CARD_GAP = 2

const useStyles = tss.create(({ theme }) => ({
    innerWrapper: {
        display: 'grid',
        gridAutoFlow: 'column',
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        gap: theme.spacing(CARD_GAP),
    },
    isScrollable: {
        '& > *': {
            scrollSnapAlign: 'start',
        },
    },
}))

const CardSlider = ({
    sx,
    scrollableMarginRight,
    children,
    parentHorizontalSpacing = 0,
}: CardSliderProps): ReactElement => {
    const { classes, cx } = useStyles()
    const theme = useTheme()
    const [width, setWidth] = useState(0)
    const [isSliderScrollable, setIsSliderScrollable] = useState(false)
    const { width: windowWidth } = useWindowSize()
    const ref = useRef<HTMLDivElement>(null)

    useLayoutEffect(() => {
        if (!ref.current) {
            return
        }

        let containerWidth = ref.current.offsetWidth
        const numOfCards = React.Children.count(children)
        const cardGap = CARD_GAP * 8

        const minCardWidthWithGap = MIN_CARD_WIDTH + cardGap
        const spaceNeededForAllCards = minCardWidthWithGap * numOfCards - cardGap
        const shouldScroll = spaceNeededForAllCards > containerWidth

        setIsSliderScrollable(shouldScroll)

        // we have enough space, no need to adjust width
        if (!shouldScroll) {
            setWidth(MIN_CARD_WIDTH)
            return
        }

        // the user wishes to overwrite the right margin, so we increase the container width
        if (scrollableMarginRight) {
            containerWidth += scrollableMarginRight * 8
        }

        // we calculate the amount of pixels the right container edge is away from the end of the last gap
        const distanceToLastGap = containerWidth % minCardWidthWithGap

        // the amount of cards that would be visible in the container
        let numOfCardsToDistributeSpaceTo = Math.floor(containerWidth / minCardWidthWithGap) + 1

        // spaceToDistribute is the pixel amount that the right container edge is
        // away from what we consider a safe zone (e.g. SAFE_ZONE_WIDTH pixels to each side of the card)
        // The plan is to distribute evenly that amount to the cards that would be visible in the container
        let spaceToDistribute = 0
        if (spaceNeededForAllCards <= containerWidth) {
            // it is possible that the container width increased after `scrollableMarginRight`
            spaceToDistribute = containerWidth - spaceNeededForAllCards + cardGap + SAFE_ZONE_WIDTH
        } else if (distanceToLastGap <= SAFE_ZONE_WIDTH) {
            // we are over the left unsafe zone of a card
            spaceToDistribute = SAFE_ZONE_WIDTH + cardGap + distanceToLastGap
            numOfCardsToDistributeSpaceTo -= 1
        } else if (distanceToLastGap >= MIN_CARD_WIDTH - SAFE_ZONE_WIDTH) {
            // we are over the right unsafe zone of a card
            spaceToDistribute = distanceToLastGap - MIN_CARD_WIDTH + SAFE_ZONE_WIDTH
        }

        setWidth(MIN_CARD_WIDTH + Math.ceil(spaceToDistribute / numOfCardsToDistributeSpaceTo))
    }, [children, scrollableMarginRight, windowWidth])

    return (
        <Box ref={ref}>
            {width ? (
                <Box
                    className={cx(classes.innerWrapper, isSliderScrollable && classes.isScrollable)}
                    style={{
                        gridTemplateColumns: isSliderScrollable ? `${width}px` : '1fr',
                        gridAutoColumns: isSliderScrollable ? `${width}px` : '1fr',
                        marginRight:
                            isSliderScrollable && scrollableMarginRight
                                ? theme.spacing(-1 * scrollableMarginRight)
                                : undefined,
                        maxWidth: windowWidth
                            ? `calc(${windowWidth}px - ${theme.spacing(parentHorizontalSpacing * 2)})`
                            : undefined,
                    }}
                    sx={sx}
                >
                    {children}
                </Box>
            ) : null}
        </Box>
    )
}

export default CardSlider
